import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase, authService } from './supabase'

// Create Supabase context
const SupabaseContext = createContext({})

// Custom hook to use Supabase context
export const useSupabase = () => {
  const context = useContext(SupabaseContext)
  if (!context) {
    throw new Error('useSupabase must be used within a SupabaseProvider')
  }
  return context
}

// Supabase Provider component
export const SupabaseProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState(null)

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        // Silently handle "Auth session missing" - it's expected when user is not logged in
        if (error) {
          // Only log unexpected errors
          if (error.message !== 'Auth session missing!' && error.name !== 'AuthSessionMissingError') {
            console.error('Error getting session:', error)
          }
        } else {
          setSession(session)
          setUser(session?.user ?? null)
        }
      } catch (err) {
        // Silently handle auth session errors
        if (err?.message !== 'Auth session missing!' && err?.name !== 'AuthSessionMissingError') {
          console.error('Unexpected error getting session:', err)
        }
      } finally {
        setLoading(false)
      }
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        try {
          setSession(session)
          setUser(session?.user ?? null)
        } catch (err) {
          // Silently handle auth errors
          if (err?.message !== 'Auth session missing!' && err?.name !== 'AuthSessionMissingError') {
            console.error('Error in auth state change:', err)
          }
        } finally {
          setLoading(false)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  // Auth methods
  const signUp = async (email, password, userData = {}) => {
    try {
      setLoading(true)
      const { data, error } = await authService.signUp(email, password, userData)
      if (error) throw error
      return data
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    try {
      setLoading(true)
      const { data, error } = await authService.signIn(email, password)
      if (error) throw error
      return data
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      await authService.signOut()
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    supabase
  }

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  )
}

export default SupabaseProvider
